import React from 'react';
import { Phone, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ContactPointsEditor from './ContactPointsEditor';
import { emptyContactPointRow, type ContactPointRow } from './contactPoints';

interface OrganizationPhonesEditorProps {
  value: ContactPointRow[];
  onChange: (phones: ContactPointRow[]) => void;
  disabled?: boolean;
}

const OrganizationPhonesEditor: React.FC<OrganizationPhonesEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { colors } = useTheme();
  const phones = value.length > 0 ? value : [emptyContactPointRow('PHONE', true)];

  const updatePhones = (nextPhones: ContactPointRow[]) => {
    onChange(nextPhones.length > 0 ? nextPhones : [emptyContactPointRow('PHONE', true)]);
  };

  const addPhone = () => {
    updatePhones([...phones, emptyContactPointRow('PHONE', phones.length === 0)]);
  };

  const actionButtonClass = cn(
    'inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2 text-xs font-medium transition-colors',
    colors.border,
    colors.textSecondary,
    'disabled:cursor-not-allowed disabled:opacity-50',
    'hover:border-indigo-400 hover:bg-indigo-50/60 hover:text-indigo-700',
    'dark:hover:border-indigo-700 dark:hover:bg-indigo-950/20 dark:hover:text-indigo-300',
  );

  return (
    <div
      className={cn(
        'min-w-0 w-full overflow-hidden rounded-lg border',
        colors.border,
        'bg-white dark:bg-gray-950/20',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b px-3 py-2',
          colors.border,
          'bg-indigo-50/60 dark:bg-indigo-950/15',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Phone className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <p className={cn('text-sm font-semibold', colors.text)}>Phones</p>
          <span
            className={cn(
              'rounded-full px-1.5 py-px text-[10px] font-semibold',
              'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300',
            )}
          >
            {phones.length}
          </span>
        </div>
        <button
          type="button"
          onClick={addPhone}
          disabled={disabled}
          className={actionButtonClass}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add
        </button>
      </div>

      <div className="px-3 py-2.5">
        <ContactPointsEditor
          kind="PHONE"
          label="Phones"
          value={phones}
          onChange={updatePhones}
          disabled={disabled}
          showPersonFields={false}
          embedded
          compact
          hideHeader
        />
      </div>
    </div>
  );
};

export default OrganizationPhonesEditor;
